import { Menu, MenuItem, Box } from '@mui/material';
import {
  Search as SearchIcon,
  TextFields as ContainsIcon,
  SearchOff as NotContainsIcon,
  FormatAlignLeft as StartsWithIcon,
  FormatAlignRight as EndsWithIcon,
  DragHandle as EqualsIcon,
  NotInterested as NotEqualsIcon,
  ChevronLeft as LessThanIcon,
  ChevronRight as GreaterThanIcon,
} from '@mui/icons-material';
import type { Table } from '@tanstack/react-table';
import type { ColumnMetadata } from '../../../types';
import type { FilterMenuAnchor, FilterOperators } from '../types';
import { isDateColumn, isNumberColumn } from '../utils';

type FilterOperatorMenuProps<T = unknown> = {
  filterMenuAnchor: FilterMenuAnchor;
  setFilterMenuAnchor: React.Dispatch<React.SetStateAction<FilterMenuAnchor>>;
  setFilterOperators: React.Dispatch<React.SetStateAction<FilterOperators>>;
  columnMetadataMap: Map<string, ColumnMetadata>;
  table: Table<T>;
};

/**
 * Menu component for selecting filter operators
 * Displays different operators based on column type (text vs date/number)
 */
export const FilterOperatorMenu = <T = unknown,>({
  filterMenuAnchor,
  setFilterMenuAnchor,
  setFilterOperators,
  columnMetadataMap,
  table,
}: FilterOperatorMenuProps<T>) => {
  const currentColumnMeta = filterMenuAnchor.columnId
    ? columnMetadataMap.get(filterMenuAnchor.columnId)
    : null;

  const isDateCol = isDateColumn(currentColumnMeta ?? undefined);
  const isNumberCol = isNumberColumn(currentColumnMeta ?? undefined);

  // Determine if this column has an active filter (operator or value set)
  const activeColumn = filterMenuAnchor.columnId
    ? table.getAllColumns().find((c) => c.id === filterMenuAnchor.columnId)
    : null;
  const currentFilterValue = activeColumn?.getFilterValue();
  const hasActiveFilter =
    currentFilterValue != null &&
    currentFilterValue !== '' &&
    !(
      typeof currentFilterValue === 'object' &&
      (currentFilterValue as { val?: string }).val === '' &&
      (currentFilterValue as { op?: string }).op == null
    );

  const handleOperatorSelect = (operator: string) => {
    if (filterMenuAnchor.columnId) {
      setFilterOperators((prev) => ({
        ...prev,
        [filterMenuAnchor.columnId!]: operator,
      }));
      const column = table.getAllColumns().find((c) => c.id === filterMenuAnchor.columnId);
      const current = column?.getFilterValue();
      const currentVal =
        current != null && typeof current === 'object'
          ? ((current as { val?: string }).val ?? '')
          : ((current as string) ?? '');
      if (currentVal !== undefined && currentVal !== null) {
        column?.setFilterValue({ op: operator, val: currentVal });
      }
    }
    setFilterMenuAnchor({ element: null, columnId: null });
  };

  const handleReset = () => {
    if (!hasActiveFilter) return;
    if (filterMenuAnchor.columnId) {
      setFilterOperators((prev) => {
        const newOps = { ...prev };
        delete newOps[filterMenuAnchor.columnId!];
        return newOps;
      });
      // Also clear the filter value
      const column = table.getAllColumns().find((c) => c.id === filterMenuAnchor.columnId);
      column?.setFilterValue(undefined);
    }
    setFilterMenuAnchor({ element: null, columnId: null });
  };

  const resetMenuItem = (
    <MenuItem
      key="reset"
      onClick={handleReset}
      disabled={!hasActiveFilter}
      sx={{
        color: hasActiveFilter ? 'error.main' : undefined,
        '&.Mui-disabled': {
          color: 'error.main',
          opacity: 0.38,
        },
        '& .MuiSvgIcon-root': {
          color: 'inherit',
        },
      }}
    >
      <SearchIcon fontSize="small" sx={{ mr: 1 }} />
      Reset
    </MenuItem>
  );

  return (
    <Menu
      anchorEl={filterMenuAnchor.element}
      open={Boolean(filterMenuAnchor.element)}
      onClose={() => setFilterMenuAnchor({ element: null, columnId: null })}
      disableScrollLock
    >
      {isDateCol || isNumberCol
        ? [
            // Date/Number field operators
            <MenuItem key="eq" onClick={() => handleOperatorSelect('=')}>
              <EqualsIcon fontSize="small" sx={{ mr: 1 }} />
              Equals
            </MenuItem>,
            <MenuItem key="neq" onClick={() => handleOperatorSelect('<>')}>
              <NotEqualsIcon fontSize="small" sx={{ mr: 1 }} />
              Does not equal
            </MenuItem>,
            <MenuItem key="lt" onClick={() => handleOperatorSelect('<')}>
              <LessThanIcon fontSize="small" sx={{ mr: 1 }} />
              Less than
            </MenuItem>,
            <MenuItem key="gt" onClick={() => handleOperatorSelect('>')}>
              <GreaterThanIcon fontSize="small" sx={{ mr: 1 }} />
              Greater than
            </MenuItem>,
            <MenuItem key="lte" onClick={() => handleOperatorSelect('<=')}>
              <Box component="span" sx={{ mr: 1, fontSize: '1.1rem', fontWeight: 'bold' }}>
                ≤
              </Box>
              Less than or equal to
            </MenuItem>,
            <MenuItem key="gte" onClick={() => handleOperatorSelect('>=')}>
              <Box component="span" sx={{ mr: 1, fontSize: '1.1rem', fontWeight: 'bold' }}>
                ≥
              </Box>
              Greater than or equal to
            </MenuItem>,
            <MenuItem key="between" onClick={() => handleOperatorSelect('between')}>
              <Box component="span" sx={{ mr: 1, fontSize: 'small' }}>
                ⟷
              </Box>
              Between
            </MenuItem>,
            resetMenuItem,
          ]
        : [
            // Text field operators
            <MenuItem key="contains" onClick={() => handleOperatorSelect('contains')}>
              <ContainsIcon fontSize="small" sx={{ mr: 1 }} />
              Contains
            </MenuItem>,
            <MenuItem key="notcontains" onClick={() => handleOperatorSelect('notcontains')}>
              <NotContainsIcon fontSize="small" sx={{ mr: 1 }} />
              Does not contain
            </MenuItem>,
            <MenuItem key="startswith" onClick={() => handleOperatorSelect('startswith')}>
              <StartsWithIcon fontSize="small" sx={{ mr: 1 }} />
              Starts with
            </MenuItem>,
            <MenuItem key="endswith" onClick={() => handleOperatorSelect('endswith')}>
              <EndsWithIcon fontSize="small" sx={{ mr: 1 }} />
              Ends with
            </MenuItem>,
            <MenuItem key="eq" onClick={() => handleOperatorSelect('=')}>
              <EqualsIcon fontSize="small" sx={{ mr: 1 }} />
              Equals
            </MenuItem>,
            <MenuItem key="neq" onClick={() => handleOperatorSelect('<>')}>
              <NotEqualsIcon fontSize="small" sx={{ mr: 1 }} />
              Does not equal
            </MenuItem>,
            resetMenuItem,
          ]}
    </Menu>
  );
};
